import { useEffect, useState } from 'react';

import useAccessBadge from '../hooks/useAccessBadge';

const hardCodedLocale = 'en';

export function useDetectBrowserTranslation(): {
  browserTranslation: "idk; it's microsoft edge" | string | null;
} {
  const chromeFirefoxSafariBrowserTranslation = useDetectChromeFirefoxSafariBrowserTranslation();
  const microsoftEdgeBrowserTranslation = useDetectMicrosoftEdgeBrowserTranslation();
  const browserTranslation =
    microsoftEdgeBrowserTranslation ?? chromeFirefoxSafariBrowserTranslation;

  return {
    browserTranslation,
  };
}

function useDetectMicrosoftEdgeBrowserTranslation() {
  const [browserTranslation, setBrowserTranslation] = useState<
    "idk; it's microsoft edge" | null
  >(null);
  useEffect(() => {
    const title = document.getElementsByTagName('title')[0];
    const detectTranslationMicrosoftEdge = () => {
      if (title?.hasAttribute('_msttexthash')) {
        setBrowserTranslation("idk; it's microsoft edge");
      } else {
        setBrowserTranslation(null);
      }
    };
    detectTranslationMicrosoftEdge();

    const observer = new MutationObserver(detectTranslationMicrosoftEdge);
    observer.observe(title, {
      attributes: true,
      attributeFilter: ['_msttexthash'],
      childList: false,
      characterData: false,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return browserTranslation;
}

function useDetectChromeFirefoxSafariBrowserTranslation() {
  const [browserTranslation, setBrowserTranslation] = useState<string | null>(null);
  const { accessBadge, isFetched } = useAccessBadge();
  const preferredLocale = accessBadge?.user.preferredLocale;
  const htmlLang = useHtmlLang();

  useEffect(() => {
    if (preferredLocale) {
      // if preferred locale is fetched, but doesn't match lang property, that means user is using browser translation
      if (htmlLang !== preferredLocale) {
        setBrowserTranslation(htmlLang);
      } else {
        setBrowserTranslation(null);
      }
    }
    if (isFetched && !preferredLocale) {
      // Compare base language codes (en vs es, fr, etc.) instead of full locale codes
      const htmlBaseLang = htmlLang?.split('-')[0];
      const isEnglishVariant = htmlBaseLang === hardCodedLocale;

      if (!isEnglishVariant) {
        setBrowserTranslation(htmlLang);
      } else {
        setBrowserTranslation(null);
      }
    }
  }, [preferredLocale, isFetched, htmlLang]);

  return browserTranslation;
}

export function useHtmlLang() {
  const [htmlLang, setHtmlLang] = useState<string | null>(null);
  useEffect(() => {
    const detectLang = () => {
      const lang = document.documentElement.lang;
      if (lang !== htmlLang) {
        setHtmlLang(lang);
      }
    };
    // Initial check
    detectLang();

    // Observe changes to the html element's attributes
    const observer = new MutationObserver(detectLang);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
      childList: false,
      characterData: false,
    });

    return () => {
      observer.disconnect();
    };
  }, [htmlLang]);
  return htmlLang;
}
